import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api';
import CaseReferenceAddModal from './modals/CaseReference/CaseReferenceAddModal';
import Pagination from '../Tools/Pagination';

const CaseReferenceTab = () => {
  const [data, setData] = useState([]);
  const [submitdata, setSubmitData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    type: '',
    specialities: [],
    assets: [],
    location_id: '',
    area_id: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    specialities: '',
    assets: '',
    page: 1,
    limit: 10
  });
  const [totalRecords, setTotalRecords] = useState(0);

  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/casereference', {
        params: filters
      });

      setData(response.data || []);
      setTotalRecords(response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      showAlert('Failed to fetch data', false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleSave = async (formPayload) => {
  try {
    setIsLoading(true);
    
    // Prepare the payload exactly as the API expects
    const payload = {
      case_id: isEditMode ? parseInt(formPayload.id) : undefined,
      specialities: formPayload.specialities
        .filter(s => s) // Remove empty values
        .map(s => parseInt(s)), // Convert to numbers
      assets: formPayload.assets
        .filter(asset => asset.name && asset.quantity) // Remove empty assets
        .map(asset => ({
          id: parseInt(asset.name), // Use the asset ID (from name field)
          quantity: parseInt(asset.quantity) || 1
        })),
      location_id: parseInt(formPayload.location_id),
      area_id: parseInt(formPayload.area_id)
    };

    // Remove case_id if not in edit mode
    if (!isEditMode) {
      delete payload.case_id;
    }

    console.log('Sending payload:', payload); // For debugging

    const response = await axiosInstance.post('/casereference', payload);
    setSubmitData(response.data || []);
    
    showAlert(isEditMode ? 'Case Reference updated successfully!' : 'Case Reference added successfully!');
    setShowModal(false);
    fetchData();
  } catch (error) {
    console.error('Save failed:', {
      error: error.response?.data,
      status: error.response?.status,
      config: error.config
    });
    showAlert(error.response?.data?.message || 'Failed to store case references', false);
  } finally {
    setIsLoading(false);
  }
};

  const handleEdit = (item) => {
  setIsEditMode(true);
  setFormData({
    id: item?.id?.toString() || '',
    name: item?.case_name || '',
    type: item?.case_type || '',
    specialities: item?.specialities?.length 
      ? item.specialities.map(s => s?.speciality_id?.toString() || '') 
      : [''],
    assets: item?.assets?.length
      ? item.assets.map(a => ({
          id: a?.asset_id?.toString() || '',
          name: a?.asset_id?.toString() || '', // Using asset_id as both id and name
          quantity: a?.quantity?.toString() || ''
        }))
      : [{ id: '', name: '', quantity: '' }],
    location_id: item?.location_id?.toString() || '',
    area_id: item?.area_id?.toString() || ''
  });
  setShowModal(true);
};

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        setIsLoading(true);
        await axiosInstance.delete(`/casereference/${id}`);
        showAlert('Deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Delete failed:', error);
        showAlert('Delete failed', false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="tab-pane active" id="casereference" role="tabpanel">
      <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-lg-3 col-12 p-2">
              <input
                type="text"
                name="search"
                className="form-control"
                placeholder="Search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-lg-4 col-12 p-2">
              <select
                name="specialities"
                className="form-control form-select"
                value={filters.specialities}
                onChange={handleFilterChange}
              >
                <option value="">Select Speciality</option>
                <option value="1">Radiology</option>
                <option value="2">Carniology</option>
                <option value="5">Padiatration</option>
              </select>
            </div>
            <div className="form-group col-lg-3 col-12 p-2">
              <select
                name="assets"
                className="form-control form-select"
                value={filters.assets}
                onChange={handleFilterChange}
              >
                <option value="">Select Asset</option>
                <option value="1">Bed</option>
                <option value="2">Vantilator</option>
                <option value="3">Gas</option>
              </select>
            </div>
          </form>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Specialities</th>
                  <th>Assets</th>
                  <th>Location</th>
                  <th>Area</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.case_name}</td>
                      <td>{item.case_type}</td>
                      <td>
                        {item.specialities?.map(s => s.speciality_name).join(', ') || 'None'}
                      </td>
                      <td>
                        {item.assets?.map(a => (
                          <div key={a.id}>
                            {a.asset?.assets_name} (Qty: {a.quantity})
                          </div>
                        )) || 'None'}
                      </td>
                      <td>{item.location?.location_name || 'None'}</td>
                      <td>{item.area?.area_name || 'None'}</td>
                      <td>
                        <button className="h4 me-3 border-0 bg-transparent" onClick={() => handleEdit(item)}>
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button className="h4 border-0 bg-transparent" onClick={() => handleDelete(item.id)}>
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">No data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {totalRecords > 0 && (
        <Pagination
          total={totalRecords}
          limit={filters.limit}
          page={filters.page}
          onPageChange={handlePageChange}
        />
      )}
      
      

      <CaseReferenceAddModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        formData={formData}
        isEditMode={isEditMode}
      />
    </div>
  );
};

export default CaseReferenceTab;